import { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router";
import { ChevronLeft } from "lucide-react";
import { fetchWatchFeed, type WatchFeedItem } from "~/api/watch";
import { reportUnexpectedError } from "~/api/client";
import { useUserStore } from "~/lib/stores/user";
import { ProductVideoCard } from "~/components/common/ProductVideoCard";
import { EntertainmentVideoCard } from "~/components/common/EntertainmentVideoCard";
import { VideoSlide } from "~/components/common/VideoSlide";
import { FullscreenVideoModal } from "~/components/common/FullscreenVideoModal";
import { Button } from "~/components/ui/button";
import PageContainer from "~/components/common/PageContainer";
import Footer from "~/components/common/Footer";
import { useIsMobile } from "~/lib/hooks/useIsMobile";
import { VideoCardSkeleton } from "~/components/skeleton/VideoCardSkeleton";
import { Skeleton } from "~/components/ui/skeleton";
import { isEntertainment } from "~/lib/utils";

function DesktopVideoGrid({
  items,
  isLoading,
  onLoadMore,
  hasMore,
  onSelectItem,
}: {
  items: WatchFeedItem[];
  isLoading: boolean;
  onLoadMore: () => void;
  hasMore: boolean;
  onSelectItem: (item: WatchFeedItem) => void;
}) {
  if (isLoading && items.length === 0) {
    return (
      <div className="mx-auto max-w-[1392px] px-4 py-8">
        <div className="mb-6">
          <h1 className="text-xl font-bold">Video deals</h1>
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-6 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
          {Array.from({ length: 12 }).map((_, i) => (
            <VideoCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1392px] px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Video deals</h1>
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-6 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
        {items.map(item => {
          return isEntertainment(item) ? (
            <EntertainmentVideoCard
              key={item.feedItemId}
              data={{
                feedItemId: item.feedItemId,
                videoUrl: item.videoUrl ?? "",
                thumbnail: item.thumbnail,
                viewCount: item.viewCount,
                likeCount: item.likeCount,
              }}
              onClick={() => onSelectItem(item)}
            />
          ) : (
            <ProductVideoCard
              key={item.feedItemId}
              data={{
                feedItemId: item.feedItemId,
                videoUrl: item.videoUrl ?? "",
                title: item.title ?? "",
                viewCount: item.viewCount,
                likeCount: item.likeCount,
              }}
              onClick={() => onSelectItem(item)}
            />
          );
        })}
      </div>

      {items.length > 0 && hasMore && (
        <div className="mt-6 flex justify-center py-2">
          <Button variant="ghost" onClick={onLoadMore} disabled={isLoading}>
            {isLoading ? "Loading..." : "Load more"}
          </Button>
        </div>
      )}
    </div>
  );
}

function MobileVideoFeed({
  items,
  isLoading,
  onLoadMore,
}: {
  items: WatchFeedItem[];
  isLoading: boolean;
  onLoadMore: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const lastSlideWrapperRef = useRef<HTMLDivElement | null>(null);
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  useEffect(() => {
    const target = lastSlideWrapperRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            onLoadMoreRef.current();
          }
        });
      },
      { threshold: [0.5] }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [items]);

  if (isLoading && items.length === 0) {
    return (
      <div className="flex h-dvh w-full items-center justify-center bg-black">
        <Skeleton className="h-full w-full" />
      </div>
    );
  }

  return (
    <div ref={containerRef} className="scrollbar-hide h-dvh w-full snap-y snap-mandatory overflow-y-scroll bg-black">
      <Link
        to="/"
        className="fixed top-4 left-4 z-50 flex size-10 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-sm hover:bg-black/70"
      >
        <ChevronLeft className="size-6" />
      </Link>

      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        return (
          <div key={item.feedItemId} ref={isLast ? lastSlideWrapperRef : null} className="h-dvh w-full snap-start">
            <VideoSlide
              trackWatch
              surface="watch_feed"
              data={{
                feedItemId: item.feedItemId,
                videoUrl: item.videoUrl ?? "",
                thumbnail: item.thumbnail,
                title: !isEntertainment(item) ? item.title : undefined,
                dealId: item.dealId,
                viewCount: item.viewCount,
                likeCount: item.likeCount,
                contentType: isEntertainment(item) ? "entertainment" : "product",
              }}
            />
          </div>
        );
      })}
    </div>
  );
}

export default function WatchFeedPage() {
  // A getter, not a subscription: the token is only needed at request time, and having
  // it as a dependency made the effect fire once before the session settled and again
  // after, discarding the first result.
  const readToken = useCallback(() => useUserStore.getState().token, []);
  const isMobile = useIsMobile();
  const [items, setItems] = useState<WatchFeedItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [selectedItem, setSelectedItem] = useState<WatchFeedItem | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Initial fetch
  useEffect(() => {
    let cancelled = false;

    setIsLoading(true);
    setHasMore(true);
    const fetchCount = isMobile ? 10 : 12;

    fetchWatchFeed(readToken, { count: fetchCount })
      .then(data => {
        if (!cancelled) {
          const feedItems = data.feedItems ?? [];
          const seen = new Set<string>();
          const deduped = feedItems.filter(item => {
            if (seen.has(item.feedItemId)) return false;
            seen.add(item.feedItemId);
            return true;
          });
          setItems(deduped);
        }
      })
      .catch(error => {
        reportUnexpectedError(error);
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isMobile, readToken]);

  const handleLoadMore = useCallback(() => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    fetchWatchFeed(readToken, { count: isMobile ? 10 : 12 })
      .then(data => {
        if (!mountedRef.current) return;
        const feedItems = data.feedItems ?? [];
        let reachedEnd = false;
        setItems(prev => {
          const existingIds = new Set(prev.map(item => item.feedItemId));
          const newItems = feedItems.filter(item => !existingIds.has(item.feedItemId));
          if (newItems.length === 0) {
            reachedEnd = true;
            return prev;
          }
          return [...prev, ...newItems];
        });
        if (reachedEnd) setHasMore(false);
      })
      .catch(e => {
        console.error(e);
        reportUnexpectedError(e);
      })
      .finally(() => {
        if (!mountedRef.current) return;
        setIsLoadingMore(false);
      });
  }, [readToken, isLoadingMore, hasMore, isMobile]);

  if (isMobile) {
    return <MobileVideoFeed items={items} isLoading={isLoading} onLoadMore={handleLoadMore} />;
  }

  return (
    <PageContainer>
      <DesktopVideoGrid
        items={items}
        isLoading={isLoading || isLoadingMore}
        onLoadMore={handleLoadMore}
        hasMore={hasMore}
        onSelectItem={setSelectedItem}
      />
      <Footer />

      {selectedItem && (
        <FullscreenVideoModal
          open={!!selectedItem}
          onOpenChange={open => {
            if (!open) setSelectedItem(null);
          }}
          surface="watch_modal"
          data={{
            feedItemId: selectedItem.feedItemId,
            videoUrl: selectedItem.videoUrl ?? "",
            thumbnail: selectedItem.thumbnail,
            title: !isEntertainment(selectedItem) ? selectedItem.title : undefined,
            dealId: selectedItem.dealId,
            viewCount: selectedItem.viewCount,
            likeCount: selectedItem.likeCount,
            contentType: isEntertainment(selectedItem) ? "entertainment" : "product",
          }}
        />
      )}
    </PageContainer>
  );
}
